import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoFocusTextarea from './ui/AutoFocusTextarea';
import ErrorBoundary from './ErrorBoundary';
import Button from './ui/button';
import MoodSlider from './MoodSlider';
import { useToast } from '../hooks/useToast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface EveningCheckInProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface EveningQuestions {
  progressQuestion: string;
  goalOutcomeQuestion: string;
  biggestWinQuestion: string;
  obstacleQuestion: string;
  tomorrowQuestion: string;
}

// Get personalized evening questions
const getEveningQuestions = (): EveningQuestions => {
  const onboardingAnswers = localStorage.getItem('onboardingAnswers');
  let priority = 'your growth goal';
  
  if (onboardingAnswers) {
    try {
      const answers = JSON.parse(onboardingAnswers);
      priority = (answers.priority || 'your growth goal').toLowerCase();
    } catch (error) {
      console.error('Error parsing onboarding answers:', error);
    }
  }
  
  return {
    progressQuestion: `Rate today's overall progress`,
    goalOutcomeQuestion: `How did your morning goal go?`,
    biggestWinQuestion: `What was your biggest win today?`,
    obstacleQuestion: `What obstacle did you navigate or discover?`,
    tomorrowQuestion: `What's tomorrow's top priority related to ${priority}?`
  };
};

const EveningCheckIn: React.FC<EveningCheckInProps> = ({ onSuccess, onCancel }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({
    progressRating: 3,
    goalOutcome: '',
    biggestWin: '',
    obstacle: '',
    tomorrowPriority: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTime] = useState(Date.now());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const questions = getEveningQuestions();
  const questionTypes = useMemo(() => ['rating', 'text', 'text', 'text', 'text'] as const, []);
  const questionLabels = useMemo(() => [
    questions.progressQuestion,
    questions.goalOutcomeQuestion,
    questions.biggestWinQuestion,
    questions.obstacleQuestion,
    questions.tomorrowQuestion
  ], [questions]);

  // Auto-focus textarea when it becomes visible
  useEffect(() => {
    if (questionTypes[currentQuestionIndex] === 'text' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentQuestionIndex, questionTypes]);

  const handleNext = () => {
    // Validate current response
    const currentType = questionTypes[currentQuestionIndex];
    if (currentType === 'text') {
      const currentValue = getCurrentTextValue();
      if (!currentValue.trim()) {
        showToast('Please provide an answer before continuing', 'error');
        return;
      }
    }

    if (currentQuestionIndex < questionLabels.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const getCurrentTextValue = () => {
    switch (currentQuestionIndex) {
      case 1: return responses.goalOutcome;
      case 2: return responses.biggestWin;
      case 3: return responses.obstacle;
      case 4: return responses.tomorrowPriority;
      default: return '';
    }
  };

  const handleMoodSliderContinue = (moodValue: number) => {
    if (currentQuestionIndex === 0) {
      setResponses(prev => ({ ...prev, progressRating: moodValue }));
      // Auto-advance to next question after mood selection
      if (currentQuestionIndex < questionLabels.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }
  };

  const handleTextChange = (text: string) => {
    switch (currentQuestionIndex) {
      case 1:
        setResponses(prev => ({ ...prev, goalOutcome: text }));
        break;
      case 2:
        setResponses(prev => ({ ...prev, biggestWin: text }));
        break;
      case 3:
        setResponses(prev => ({ ...prev, obstacle: text }));
        break;
      case 4:
        setResponses(prev => ({ ...prev, tomorrowPriority: text }));
        break;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const responseTime = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showToast('Please log in to continue', 'error');
        navigate('/signup');
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-auth-token': token
      };
      
      // Add access granted header if password protection was bypassed
      const accessGranted = localStorage.getItem('thinkmate_access_granted');
      if (accessGranted === 'true') {
        headers['x-access-granted'] = 'true';
      }

      const response = await fetch(`${API_URL}/api/daily-checkins/evening`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          progressRating: responses.progressRating,
          goalOutcome: responses.goalOutcome.trim(),
          biggestWin: responses.biggestWin.trim(),
          obstacle: responses.obstacle.trim(),
          tomorrowPriority: responses.tomorrowPriority.trim(),
          responseTime
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to submit evening check-in');
      }

      showToast('Evening check-in completed! 🌙', 'success');
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/dashboard');
      }

    } catch (error) {
      console.error('Evening check-in submission error:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to submit check-in', 
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  const currentQuestionType = questionTypes[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questionLabels.length) * 100;
  const currentTextValue = getCurrentTextValue();

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-6 md:p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 md:p-6 space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full p-3">
                <span className="text-2xl">🌙</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">Good Evening!</h1>
            <p className="text-text-secondary text-sm">Let's reflect on your day and plan ahead</p>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-text-secondary text-center">
            Question {currentQuestionIndex + 1} of {questionLabels.length}
          </p>

          {/* Question */}
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-text-primary text-center">
              {questionLabels[currentQuestionIndex]}
            </h2>

            {/* Mood Slider Input */}
            {currentQuestionType === 'rating' && (
              <div className="space-y-1">
                <MoodSlider
                  onContinue={handleMoodSliderContinue}
                  disabled={isSubmitting}
                  className="py-4"
                />
              </div>
            )}

            {/* Text Input */}
            {currentQuestionType === 'text' && (
              <div className="space-y-2">
                <AutoFocusTextarea
                  ref={textareaRef}
                  value={currentTextValue}
                  onChange={(e) => handleTextChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={getPlaceholderText(currentQuestionIndex)}
                  className="w-full p-5 md:p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[120px] md:min-h-[100px] text-base md:text-sm text-text-primary"
                  maxLength={500}
                />
                <p className="text-xs text-text-secondary text-right">
                  {currentTextValue.length}/500 characters
                </p>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-4">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              variant="ghost"
              ambient={true}
              className="px-5 py-3 md:px-4 md:py-2 disabled:text-gray-400 disabled:cursor-not-allowed min-h-[48px] md:min-h-auto flex items-center"
            >
              ← Previous
            </Button>

            <Button
              onClick={handleNext}
              disabled={isSubmitting || (currentQuestionType === 'text' && !currentTextValue.trim())}
              ambient={true}
              className={`px-6 py-3 md:py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 shadow-lg min-h-[48px] md:min-h-auto flex items-center justify-center ${
                currentQuestionType === 'rating' ? 'hidden' : ''
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting...</span>
                </div>
              ) : currentQuestionIndex === questionLabels.length - 1 ? (
                'Complete Check-in'
              ) : (
                'Next →'
              )}
            </Button>
          </div>

          {/* Cancel Link */}
          {onCancel && (
            <div className="text-center pt-2">
              <Button
                onClick={onCancel}
                variant="ghost"
                ambient={true}
                className="text-sm min-h-[44px] flex items-center justify-center"
              >
                Cancel and return to dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

// Helper function for placeholder text
const getPlaceholderText = (questionIndex: number): string => {
  switch (questionIndex) {
    case 1: return "Describe how your main goal went today...";
    case 2: return "What achievement are you most proud of?";
    case 3: return "What challenge did you face or learn about?";
    case 4: return "What's the most important thing to focus on tomorrow?";
    default: return "Share your thoughts...";
  }
};

export default EveningCheckIn;